import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, X, Save, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';

const BulkRewardUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [bulkSettings, setBulkSettings] = useState({
    reward_type: 'sticker',
    cost: 10,
    available_start: '',
    available_end: '',
    icon: '⭐',
    month: 'January'
  });
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState(1); // 1: upload, 2: spreadsheet
  const { toast } = useToast();
  const navigate = useNavigate();

  // Convert filename to readable name
  const formatName = (filename) => {
    return filename
      .replace(/\.[^/.]+$/, '') // Remove extension
      .replace(/[-_]/g, ' ') // Replace - and _ with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    setSelectedFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const processFiles = () => {
    if (selectedFiles.length === 0) {
      toast({
        title: 'No Files',
        description: 'Please select at least one image file',
        variant: 'destructive'
      });
      return;
    }

    if (!bulkSettings.available_start || !bulkSettings.available_end) {
      toast({
        title: 'Missing Dates',
        description: 'Please set availability dates',
        variant: 'destructive'
      });
      return;
    }

    const newItems = selectedFiles.map((file, index) => ({
      id: `temp-${index}`,
      name: formatName(file.name),
      reward_type: bulkSettings.reward_type,
      cost: bulkSettings.cost,
      description: '',
      available_start: bulkSettings.available_start,
      available_end: bulkSettings.available_end,
      icon: bulkSettings.icon,
      month: bulkSettings.month,
      file: file,
      preview: URL.createObjectURL(file)
    }));

    setItems(newItems);
    setStep(2);
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const deleteItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const uploadAll = async () => {
    if (items.length === 0) {
      toast({
        title: 'No Items',
        description: 'No items to upload',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    const adminToken = localStorage.getItem('admin_token');
    let successCount = 0;
    let failCount = 0;

    try {
      for (const item of items) {
        try {
          const formData = new FormData();
          formData.append('name', item.name);
          formData.append('reward_type', item.reward_type);
          formData.append('cost', item.cost);
          formData.append('description', item.description);
          // Convert date to ISO format with time
          formData.append('available_start', `${item.available_start}T00:00:00`);
          formData.append('available_end', `${item.available_end}T23:59:59`);
          formData.append('icon', item.icon);
          formData.append('month', item.month || '');  // Add month
          formData.append('image', item.file);

          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/rewards`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${adminToken}`
            },
            body: formData
          });

          if (response.ok) {
            const result = await response.json();
            successCount++;
            console.log(`✓ Uploaded ${item.name}`);
          } else {
            const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
            failCount++;
            console.error(`✗ Failed to upload ${item.name}: ${errorData.detail || response.statusText}`);
          }
        } catch (error) {
          failCount++;
          console.error(`✗ Error uploading ${item.name}:`, error.message);
        }
      }

      toast({
        title: 'Upload Complete',
        description: `Successfully uploaded ${successCount} items${failCount > 0 ? `, ${failCount} failed` : ''}`,
        variant: successCount > 0 ? 'default' : 'destructive'
      });

      if (successCount > 0) {
        navigate('/admin/rewards/manage');
      }
    } catch (error) {
      toast({
        title: 'Upload Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  if (step === 1) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AdminNav />
        <div className="max-w-4xl mx-auto p-6">

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Bulk Upload Rewards
              </CardTitle>
              <CardDescription>
                Upload multiple reward images at once and edit them in a spreadsheet
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* File Upload Area */}
              <div>
                <Label>Upload Images</Label>
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-12 text-center hover:border-blue-400 transition-colors cursor-pointer"
                  onClick={() => document.getElementById('file-input').click()}
                >
                  <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-sm text-slate-600 mb-2">
                    Drag & drop images here, or click to select
                  </p>
                  <p className="text-xs text-slate-400">
                    Supports PNG, JPG, JPEG, WebP
                  </p>
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
                {selectedFiles.length > 0 && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ {selectedFiles.length} file(s) selected
                  </p>
                )}
              </div>

              {/* Bulk Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Reward Type</Label>
                  <Select
                    value={bulkSettings.reward_type}
                    onValueChange={(value) => setBulkSettings({ ...bulkSettings, reward_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sticker">Sticker</SelectItem>
                      <SelectItem value="pet">Pet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Default Cost (tokens)</Label>
                  <Input
                    type="number"
                    value={bulkSettings.cost}
                    onChange={(e) => setBulkSettings({ ...bulkSettings, cost: parseInt(e.target.value) })}
                    min="1"
                  />
                </div>

                <div>
                  <Label>Available Start Date</Label>
                  <Input
                    type="date"
                    value={bulkSettings.available_start}
                    onChange={(e) => setBulkSettings({ ...bulkSettings, available_start: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Available End Date</Label>
                  <Input
                    type="date"
                    value={bulkSettings.available_end}
                    onChange={(e) => setBulkSettings({ ...bulkSettings, available_end: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Icon (emoji)</Label>
                  <Input
                    value={bulkSettings.icon}
                    onChange={(e) => setBulkSettings({ ...bulkSettings, icon: e.target.value })}
                    maxLength="2"
                  />
                </div>

                <div>
                  <Label>Month</Label>
                  <Select
                    value={bulkSettings.month}
                    onValueChange={(value) => setBulkSettings({ ...bulkSettings, month: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="January">January</SelectItem>
                      <SelectItem value="February">February</SelectItem>
                      <SelectItem value="March">March</SelectItem>
                      <SelectItem value="April">April</SelectItem>
                      <SelectItem value="May">May</SelectItem>
                      <SelectItem value="June">June</SelectItem>
                      <SelectItem value="July">July</SelectItem>
                      <SelectItem value="August">August</SelectItem>
                      <SelectItem value="September">September</SelectItem>
                      <SelectItem value="October">October</SelectItem>
                      <SelectItem value="November">November</SelectItem>
                      <SelectItem value="December">December</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button 
                onClick={processFiles} 
                className="w-full"
                disabled={selectedFiles.length === 0}
              >
                Continue to Spreadsheet Editor →
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Step 2: Spreadsheet Editor
  return (
    <div className="min-h-screen bg-slate-50">
      <AdminNav />
      <div className="max-w-full mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setStep(1)}>
            ← Back to Upload
          </Button>
          <div className="flex gap-2">
            <Button onClick={uploadAll} disabled={uploading || items.length === 0}>
              <Save className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : `Upload All (${items.length})`}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Edit Rewards - Spreadsheet View</CardTitle>
            <CardDescription>
              Edit names, costs, descriptions, and other details before uploading
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="p-2 text-left font-semibold text-sm">Preview</th>
                    <th className="p-2 text-left font-semibold text-sm min-w-[200px]">Name</th>
                    <th className="p-2 text-left font-semibold text-sm">Type</th>
                    <th className="p-2 text-left font-semibold text-sm">Cost</th>
                    <th className="p-2 text-left font-semibold text-sm min-w-[250px]">Description (Code-friendly)</th>
                    <th className="p-2 text-left font-semibold text-sm">Month</th>
                    <th className="p-2 text-left font-semibold text-sm">Start Date</th>
                    <th className="p-2 text-left font-semibold text-sm">End Date</th>
                    <th className="p-2 text-left font-semibold text-sm">Icon</th>
                    <th className="p-2 text-left font-semibold text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-200 hover:bg-slate-50">
                      <td className="p-2">
                        <img
                          src={item.preview}
                          alt={item.name}
                          className="w-12 h-12 rounded object-cover border border-slate-200"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.name}
                          onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                          className="text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={item.reward_type}
                          onValueChange={(value) => updateItem(item.id, 'reward_type', value)}
                        >
                          <SelectTrigger className="text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sticker">Sticker</SelectItem>
                            <SelectItem value="pet">Pet</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          value={item.cost}
                          onChange={(e) => updateItem(item.id, 'cost', parseInt(e.target.value))}
                          min="1"
                          className="text-sm w-20"
                        />
                      </td>
                      <td className="p-2">
                        <textarea
                          value={item.description}
                          onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                          placeholder="Optional description (supports code)..."
                          className="text-sm w-full min-h-[60px] p-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs resize-y"
                          style={{ whiteSpace: 'pre-wrap' }}
                        />
                      </td>
                      <td className="p-2">
                        <Select
                          value={item.month}
                          onValueChange={(value) => updateItem(item.id, 'month', value)}
                        >
                          <SelectTrigger className="text-sm w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="January">January</SelectItem>
                            <SelectItem value="February">February</SelectItem>
                            <SelectItem value="March">March</SelectItem>
                            <SelectItem value="April">April</SelectItem>
                            <SelectItem value="May">May</SelectItem>
                            <SelectItem value="June">June</SelectItem>
                            <SelectItem value="July">July</SelectItem>
                            <SelectItem value="August">August</SelectItem>
                            <SelectItem value="September">September</SelectItem>
                            <SelectItem value="October">October</SelectItem>
                            <SelectItem value="November">November</SelectItem>
                            <SelectItem value="December">December</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={item.available_start}
                          onChange={(e) => updateItem(item.id, 'available_start', e.target.value)}
                          className="text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={item.available_end}
                          onChange={(e) => updateItem(item.id, 'available_end', e.target.value)}
                          className="text-sm"
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.icon}
                          onChange={(e) => updateItem(item.id, 'icon', e.target.value)}
                          maxLength="2"
                          className="text-sm w-16"
                        />
                      </td>
                      <td className="p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem(item.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {items.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                No items to display
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BulkRewardUpload;
